export type EntranceCategory =
  | 'overworld'
  | 'interior'
  | 'grotto'
  | 'dungeon'
  | 'boss'
  | 'warp'
  | 'spawn'
  | 'owl';

export interface VanillaEntrance {
  id: string;
  zone: string;
  vanillaTo: string;
  category: EntranceCategory;
  label: string;
}

export interface EntranceConnection {
  id: string;
  zone: string;
  vanillaTo: string;
  category: EntranceCategory;
  label: string;
  actualDestination: string | null;
  actualFrom: string | null;
  linkedTo: string | null;        // ID of the linked connection (for edge resolution)
  isReverse: boolean;             // true = reverse side of a bidirectional link (no edge created)
  isTracked: boolean;
  note?: string;
}

export interface ShopItem {
  itemName: string | null;
  price: number | null;
}

export interface EntranceTrackerState {
  connections: Record<string, EntranceConnection>;
  visibleCategories: EntranceCategory[];
  visibleZones: string[];
  searchTerm: string;
  viewMode: 'graph' | 'list';
  isDecoupled: boolean;
  zoneRewards: Record<string, string[]>;
  hideDone: boolean;
  shopData: Record<string, ShopItem[]>;       // keyed by entrance id
  scrubsCount: Record<string, number>;        // keyed by entrance id
}

// ─── Dungeon Rewards (medallions + spiritual stones) ──────────

export interface DungeonReward {
  id: string;
  label: string;
  image: string;
}

import yellowMedallion from '@/core/assets/oot/gshd/yellow-medallion_32x32.png';
import greenMedallion from '@/core/assets/oot/gshd/green-medallion_32x32.png';
import redMedallion from '@/core/assets/oot/gshd/red-medallion_32x32.png';
import blueMedallion from '@/core/assets/oot/gshd/blue-medallion_32x32.png';
import purpleMedallion from '@/core/assets/oot/gshd/purple-medallion_32x32.png';
import orangeMedallion from '@/core/assets/oot/gshd/orange-medallion_32x32.png';
import kokiriStone from '@/core/assets/oot/gshd/kokiri-stone_32x32.png';
import goronStone from '@/core/assets/oot/gshd/goron-stone_32x32.png';
import zoraStone from '@/core/assets/oot/gshd/zora-stone_32x32.png';

export const DUNGEON_REWARDS: DungeonReward[] = [
  { id: 'light-medallion', label: 'Light Medallion', image: yellowMedallion },
  { id: 'forest-medallion', label: 'Forest Medallion', image: greenMedallion },
  { id: 'fire-medallion', label: 'Fire Medallion', image: redMedallion },
  { id: 'water-medallion', label: 'Water Medallion', image: blueMedallion },
  { id: 'shadow-medallion', label: 'Shadow Medallion', image: purpleMedallion },
  { id: 'spirit-medallion', label: 'Spirit Medallion', image: orangeMedallion },
  { id: 'kokiri-emerald', label: 'Kokiri Emerald', image: kokiriStone },
  { id: 'goron-ruby', label: 'Goron Ruby', image: goronStone },
  { id: 'zora-sapphire', label: 'Zora Sapphire', image: zoraStone },
];

export type ColorRegion = 'Forest' | 'Death Mountain' | 'Kakariko' | 'Zora' | 'Desert' | 'Hyrule' | 'Special';

export const ZONE_TO_COLOR_REGION: Record<string, ColorRegion> = {
  KF: 'Forest',
  LW: 'Forest',
  'LW Bridge': 'Forest',
  SFM: 'Forest',
  HF: 'Hyrule',
  MKT: 'Hyrule',
  'MKT Bridge': 'Hyrule',
  HC: 'Hyrule',
  OGC: 'Hyrule',
  LLR: 'Hyrule',
  KAK: 'Kakariko',
  'Potion Shop': 'Kakariko',
  GY: 'Kakariko',
  DMT: 'Death Mountain',
  DMC: 'Death Mountain',
  GC: 'Death Mountain',
  ZR: 'Zora',
  ZD: 'Zora',
  ZF: 'Zora',
  LH: 'Zora',
  GV: 'Desert',
  GF: 'Desert',
  Wasteland: 'Desert',
  DCol: 'Desert',
  Warps: 'Special',
  Spawns: 'Special',
  Owls: 'Special',
};


export const ENTRANCE_CATEGORY_COLORS: Record<EntranceCategory, string> = {
  overworld: '#22c55e',
  interior: '#3b82f6',
  grotto: '#a16207',
  dungeon: '#ef4444',
  boss: '#a855f7',
  warp: '#06b6d4',
  spawn: '#f97316',
  owl: '#eab308',
};

export const ENTRANCE_CATEGORY_LABELS: Record<EntranceCategory, string> = {
  overworld: 'Overworld',
  interior: 'Interior',
  grotto: 'Grotto',
  dungeon: 'Dungeon',
  boss: 'Boss Room',
  warp: 'Warp Song',
  spawn: 'Spawn',
  owl: 'Owl Flight',
};

export const ALL_ZONES = [
  'KF', 'LW', 'LW Bridge', 'SFM',
  'HF', 'MKT', 'MKT Bridge', 'HC', 'OGC', 'LLR',
  'KAK', 'Potion Shop', 'GY',
  'DMT', 'DMC', 'GC',
  'ZR', 'ZD', 'ZF', 'LH',
  'GV', 'GF', 'Wasteland', 'DCol',
  'Warps', 'Spawns', 'Owls',
] as const;

export const ALL_CATEGORIES: EntranceCategory[] = [
  'overworld', 'interior', 'grotto', 'dungeon', 'boss', 'warp', 'spawn', 'owl',
];

// ─── Dead-end destinations (pathfinding targets with no outgoing edges) ──

export interface DeadEndDestination {
  id: string;
  label: string;
  group: 'dungeon' | 'boss' | 'special';
  vanillaTo: string;
  parentZone: string;
}

export const DEAD_END_DESTINATIONS: DeadEndDestination[] = [
  // Special
  { id: 'de:ToT', label: 'Temple of Time', group: 'special', vanillaTo: 'Temple of Time', parentZone: 'MKT' },
  // Dungeons
  { id: 'de:Deku', label: 'Deku Tree', group: 'dungeon', vanillaTo: 'Deku Tree Lobby', parentZone: 'KF' },
  { id: 'de:DC', label: "Dodongo's Cavern", group: 'dungeon', vanillaTo: 'Dodongos Cavern Beginning', parentZone: 'DMT' },
  { id: 'de:Jabu', label: "Jabu Jabu's Belly", group: 'dungeon', vanillaTo: 'Jabu Jabus Belly Beginning', parentZone: 'ZF' },
  { id: 'de:Forest', label: 'Forest Temple', group: 'dungeon', vanillaTo: 'Forest Temple Lobby', parentZone: 'SFM' },
  { id: 'de:Fire', label: 'Fire Temple', group: 'dungeon', vanillaTo: 'Fire Temple Lower', parentZone: 'DMC' },
  { id: 'de:Water', label: 'Water Temple', group: 'dungeon', vanillaTo: 'Water Temple Lobby', parentZone: 'LH' },
  { id: 'de:Shadow', label: 'Shadow Temple', group: 'dungeon', vanillaTo: 'Shadow Temple Entryway', parentZone: 'GY' },
  { id: 'de:Spirit', label: 'Spirit Temple', group: 'dungeon', vanillaTo: 'Spirit Temple Lobby', parentZone: 'DCol' },
  { id: 'de:Ice', label: 'Ice Cavern', group: 'dungeon', vanillaTo: 'Ice Cavern Beginning', parentZone: 'ZF' },
  { id: 'de:BotW', label: 'Bottom of the Well', group: 'dungeon', vanillaTo: 'Bottom of the Well', parentZone: 'KAK' },
  { id: 'de:GTG', label: 'Gerudo Training Ground', group: 'dungeon', vanillaTo: 'Gerudo Training Ground Lobby', parentZone: 'GF' },
  { id: 'de:Ganon', label: "Ganon's Castle", group: 'dungeon', vanillaTo: 'Ganons Castle Lobby', parentZone: 'OGC' },
  // Boss rooms
  { id: 'de:Gohma', label: 'Gohma', group: 'boss', vanillaTo: 'Queen Gohma Boss Room', parentZone: 'KF' },
  { id: 'de:KingDodongo', label: 'King Dodongo', group: 'boss', vanillaTo: 'King Dodongo Boss Room', parentZone: 'DMT' },
  { id: 'de:Barinade', label: 'Barinade', group: 'boss', vanillaTo: 'Barinade Boss Room', parentZone: 'ZF' },
  { id: 'de:PhantomGanon', label: 'Phantom Ganon', group: 'boss', vanillaTo: 'Phantom Ganon Boss Room', parentZone: 'SFM' },
  { id: 'de:Volvagia', label: 'Volvagia', group: 'boss', vanillaTo: 'Volvagia Boss Room', parentZone: 'DMC' },
  { id: 'de:Morpha', label: 'Morpha', group: 'boss', vanillaTo: 'Morpha Boss Room', parentZone: 'LH' },
  { id: 'de:BongoBongo', label: 'Bongo Bongo', group: 'boss', vanillaTo: 'Bongo Bongo Boss Room', parentZone: 'GY' },
  { id: 'de:Twinrova', label: 'Twinrova', group: 'boss', vanillaTo: 'Twinrova Boss Room', parentZone: 'DCol' },
  { id: 'de:GanonTower', label: "Ganon's Tower", group: 'boss', vanillaTo: 'Ganons Castle Tower', parentZone: 'OGC' },
];

/** Quick lookup: dead-end ID → definition */
export const DEAD_END_BY_ID = new Map(DEAD_END_DESTINATIONS.map(d => [d.id, d]));

/** Quick lookup: vanillaTo destination name → dead-end ID */
export const VANILLA_TO_DEAD_END = new Map(DEAD_END_DESTINATIONS.map(d => [d.vanillaTo, d.id]));

// Extend color region map with dead-end IDs (inherit parent zone color)
for (const de of DEAD_END_DESTINATIONS) {
  const region = ZONE_TO_COLOR_REGION[de.parentZone];
  if (region) ZONE_TO_COLOR_REGION[de.id] = region;
}

/**
 * Entrance IDs that are inherently one-way (no reverse link when tracking).
 * e.g., the GV stream to LH — you can't swim back up the river.
 */
export const ONE_WAY_ENTRANCE_IDS = new Set([
  'GV::GV => LH (stream)',
]);
